<script setup lang="ts">
import { computed, onMounted, ref, watch } from 'vue'
import Button from 'primevue/button'
import Message from 'primevue/message'
import Skeleton from 'primevue/skeleton'
import { useAuthStore } from '@/stores/auth'
import {
  marketingApi,
  MARKETING_EXPORTS,
  type MarketingSummary,
  type DailyRow,
} from '@/services/marketing'
import AppCard from '@/components/ui/AppCard.vue'
import DateField from '@/components/ui/DateField.vue'
import SimplePaginator from '@/components/ui/SimplePaginator.vue'
import { formatCurrency, formatShortDate } from '@/utils/format'

/**
 * Panel de marketing: una tabla con un día por fila.
 *
 * Lo que el equipo mira es qué se movió cada día —pedidos, facturación y de los
 * clientes que compraron, cuántos eran nuevos—, y con eso escribe a quién
 * volver a contactar. La tabla es el panel entero: ni tarjetas ni gráficos que
 * haya que interpretar, porque la hoja de cálculo que esto sustituye tampoco
 * los tenía.
 *
 * Las columnas de pauta —mensajes, inversión, conversión, ROAS— no están aquí
 * a propósito: vienen de Meta y de la plataforma, así que van en el Excel, que
 * es donde el equipo las rellena a mano después de descargarlo.
 */

const authStore = useAuthStore()
const allowed = computed(() => authStore.hasPermission('marketing.access'))

const summary = ref<MarketingSummary | null>(null)
const loading = ref(false)
const errorMessage = ref<string | null>(null)

const dateFrom = ref('')
const dateTo = ref('')

/**
 * Página de la tabla.
 *
 * La tabla enseña 31 días: un mes natural completo en una página. Un rango más
 * largo se recorre paginando.
 */
const page = ref(1)

const totalDias = computed(() => summary.value?.daily.total ?? 0)
const lastPage = computed(() => summary.value?.daily.last_page ?? 1)

const filas = computed<DailyRow[]>(() => summary.value?.daily.data ?? [])

/**
 * Totales del rango completo, al pie de la tabla.
 *
 * Son de todo el rango y no de la página: si el panel anuncia un trimestre y el
 * pie suma solo el mes que se ve, el número de abajo contradice al de arriba.
 */
const totales = computed(
  () => summary.value?.totals ?? { orders: 0, invoiced: 0, new_customers: 0, recurring_customers: 0 },
)

function extractErrorMessage(cause: unknown): string {
  const maybeAxios = cause as { response?: { data?: { error?: { message?: string } } } }
  return maybeAxios.response?.data?.error?.message ?? 'No se pudo cargar el panel. Revisa tu conexión e inténtalo de nuevo.'
}

async function load(): Promise<void> {
  loading.value = true
  errorMessage.value = null

  try {
    const { data } = await marketingApi.summary({
      date_from: dateFrom.value === '' ? undefined : dateFrom.value,
      date_to: dateTo.value === '' ? undefined : dateTo.value,
      page_daily: page.value,
    })
    summary.value = data.data
  } catch (cause: unknown) {
    summary.value = null
    errorMessage.value = extractErrorMessage(cause)
  } finally {
    loading.value = false
  }
}

/** Cambia de página y recarga. */
async function irAPagina(nueva: number): Promise<void> {
  page.value = Math.max(1, nueva)
  await load()
}

/**
 * Reinicia el rango al mes en curso.
 *
 * El rango se manda explícito y el backend lo confirma en `data.range`: si el
 * cliente calculara el mes por su cuenta, un Excel descargado a medianoche
 * podría traer un mes distinto del que se está viendo.
 */
function resetToCurrentMonth(): void {
  const now = new Date()
  const first = new Date(now.getFullYear(), now.getMonth(), 1)
  const last = new Date(now.getFullYear(), now.getMonth() + 1, 0)

  const iso = (date: Date): string => {
    const month = String(date.getMonth() + 1).padStart(2, '0')
    const day = String(date.getDate()).padStart(2, '0')
    return `${date.getFullYear()}-${month}-${day}`
  }

  dateFrom.value = iso(first)
  dateTo.value = iso(last)
}

watch([dateFrom, dateTo], () => {
  // Cambiar el rango deja sin sentido la página en la que se estaba.
  page.value = 1
  void load()
})

/**
 * Descarga el Excel.
 *
 * Lleva el rango que se está viendo y **todos** los días, no la página de 30:
 * el fichero es el producto que el equipo se lleva, y uno a medias no se
 * distingue de uno completo hasta que falta alguien.
 */
const downloading = ref<string | null>(null)

async function download(path: string): Promise<void> {
  if (downloading.value !== null) return

  downloading.value = path
  errorMessage.value = null

  try {
    await marketingApi.download(path, {
      date_from: dateFrom.value === '' ? undefined : dateFrom.value,
      date_to: dateTo.value === '' ? undefined : dateTo.value,
    })
  } catch (cause: unknown) {
    const maybe = cause as { message?: string }
    errorMessage.value = maybe.message ?? 'No se pudo descargar el fichero.'
  } finally {
    downloading.value = null
  }
}

onMounted(() => {
  resetToCurrentMonth()
})
</script>

<template>
  <div class="space-y-4">
    <Message
      v-if="!allowed"
      severity="warn"
      :closable="false"
      variant="simple"
      role="alert"
    >
      El panel de marketing es de acceso restringido: tu rol no tiene el permiso
      <code>marketing.access</code>.
    </Message>

    <template v-else>
      <AppCard>
        <div class="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
          <form
            class="grid grid-cols-1 items-end gap-4 sm:grid-cols-[1fr_1fr_auto]"
            role="search"
            @submit.prevent="load"
          >
            <div class="min-w-0">
              <DateField
                v-model="dateFrom"
                input-id="marketing-from"
                label="Desde"
              />
            </div>

            <div class="min-w-0">
              <DateField
                v-model="dateTo"
                input-id="marketing-to"
                label="Hasta"
                :min="dateFrom"
              />
            </div>

            <Button
              label="Mes en curso"
              icon="pi pi-calendar"
              severity="secondary"
              type="button"
              class="w-full sm:w-auto"
              :loading="loading"
              @click="resetToCurrentMonth"
            />
          </form>

          <!--
            La descarga vive al lado del filtro y no en una tarjeta aparte: es
            la acción del panel, y separarla la hacía buscar cada vez.
          -->
          <Button
            v-for="exportacion in MARKETING_EXPORTS"
            :key="exportacion.key"
            :label="exportacion.label"
            icon="pi pi-download"
            severity="secondary"
            outlined
            type="button"
            class="shrink-0"
            :loading="downloading === exportacion.path"
            :disabled="downloading !== null && downloading !== exportacion.path"
            :data-testid="`marketing-export-${exportacion.key}`"
            @click="download(exportacion.path)"
          />
        </div>
      </AppCard>

      <!-- Estado de error -->
      <div
        v-if="errorMessage"
        class="flex flex-col items-center gap-3 py-12 text-center"
        role="alert"
      >
        <i class="pi pi-exclamation-triangle text-4xl text-amber-500" />
        <p class="font-medium text-surface-900 dark:text-surface-100">
          No se pudo cargar el panel
        </p>
        <p class="max-w-md text-sm text-surface-500 dark:text-surface-400">
          {{ errorMessage }}
        </p>
        <Button
          label="Reintentar"
          icon="pi pi-refresh"
          severity="secondary"
          @click="load"
        />
      </div>

      <AppCard v-else-if="loading">
        <div class="space-y-4">
          <Skeleton
            v-for="index in 6"
            :key="index"
            height="2.5rem"
          />
        </div>
      </AppCard>

      <template v-else-if="summary">
        <AppCard>
          <template #header>
            <div class="flex flex-wrap items-baseline justify-between gap-2">
              <h2 class="text-base font-semibold text-surface-900 dark:text-surface-100">
                Movimiento por día
              </h2>
              <p class="text-sm text-surface-500 dark:text-surface-400">
                {{ totalDias }} días en el rango · se muestran
                {{ summary.daily.per_page }} por página
              </p>
            </div>
          </template>

          <!--
            La tabla es de ancho completo a propósito. Con las siete columnas en
            una tarjeta de la mitad de la pantalla, «Día Semana» salía en dos
            líneas y la facturación con el número partido.
          -->
          <div class="overflow-x-auto">
            <table
              class="w-full min-w-[46rem] text-sm"
              data-testid="marketing-daily-table"
            >
              <thead>
                <tr class="border-b border-surface-200 text-left text-xs uppercase tracking-wide text-surface-500 dark:border-surface-700 dark:text-surface-400">
                  <th
                    class="whitespace-nowrap px-2 py-2 font-medium"
                    scope="col"
                  >
                    Día
                  </th>
                  <th
                    class="whitespace-nowrap px-2 py-2 font-medium"
                    scope="col"
                  >
                    Fecha
                  </th>
                  <th
                    class="whitespace-nowrap px-2 py-2 font-medium"
                    scope="col"
                  >
                    Día Semana
                  </th>
                  <th
                    class="whitespace-nowrap px-2 py-2 text-right font-medium"
                    scope="col"
                    title="Cotizaciones facturadas y ventas de tienda facturadas."
                  >
                    Pedidos
                  </th>
                  <th
                    class="whitespace-nowrap px-2 py-2 text-right font-medium"
                    scope="col"
                  >
                    Facturación
                  </th>
                  <th
                    class="whitespace-nowrap px-2 py-2 text-right font-medium"
                    scope="col"
                  >
                    Clientes Nuevos
                  </th>
                  <th
                    class="whitespace-nowrap px-2 py-2 text-right font-medium"
                    scope="col"
                  >
                    Clientes Recurrentes
                  </th>
                </tr>
              </thead>

              <tbody class="divide-y divide-surface-100 dark:divide-surface-800">
                <tr
                  v-for="fila in filas"
                  :key="fila.date"
                  :data-testid="`fila-${fila.date}`"
                >
                  <td class="whitespace-nowrap px-2 py-2 tabular-nums text-surface-500 dark:text-surface-400">
                    {{ fila.day }}
                  </td>
                  <td class="whitespace-nowrap px-2 py-2 tabular-nums text-surface-900 dark:text-surface-100">
                    {{ formatShortDate(fila.date) }}
                  </td>
                  <td class="whitespace-nowrap px-2 py-2 text-surface-700 dark:text-surface-200">
                    {{ fila.weekday }}
                  </td>
                  <td class="whitespace-nowrap px-2 py-2 text-right tabular-nums text-surface-900 dark:text-surface-100">
                    {{ fila.orders }}
                  </td>
                  <td class="whitespace-nowrap px-2 py-2 text-right tabular-nums text-surface-900 dark:text-surface-100">
                    {{ fila.invoiced === 0 ? '—' : formatCurrency(fila.invoiced) }}
                  </td>
                  <td class="whitespace-nowrap px-2 py-2 text-right tabular-nums text-surface-900 dark:text-surface-100">
                    {{ fila.new_customers }}
                  </td>
                  <td class="whitespace-nowrap px-2 py-2 text-right tabular-nums text-surface-900 dark:text-surface-100">
                    {{ fila.recurring_customers }}
                  </td>
                </tr>
              </tbody>

              <!--
                Totales al pie, y **de todo el rango** aunque se esté viendo una
                página: son el número que dice cuánto se movió en el periodo.
                Los de la pantalla y los del Excel salen de la misma cuenta.
              -->
              <tfoot class="border-t-2 border-surface-200 font-semibold dark:border-surface-700">
                <tr>
                  <td
                    class="px-2 py-2 text-xs uppercase tracking-wide text-surface-500 dark:text-surface-400"
                    colspan="3"
                  >
                    Total del rango
                  </td>
                  <td class="px-2 py-2 text-right tabular-nums text-surface-900 dark:text-surface-100">
                    {{ totales.orders }}
                  </td>
                  <td class="px-2 py-2 text-right tabular-nums text-surface-900 dark:text-surface-100">
                    {{ formatCurrency(totales.invoiced) }}
                  </td>
                  <td class="px-2 py-2 text-right tabular-nums text-surface-900 dark:text-surface-100">
                    {{ totales.new_customers }}
                  </td>
                  <td class="px-2 py-2 text-right tabular-nums text-surface-900 dark:text-surface-100">
                    {{ totales.recurring_customers }}
                  </td>
                </tr>
              </tfoot>
            </table>
          </div>

          <SimplePaginator
            v-if="lastPage > 1"
            class="mt-4"
            :page="summary.daily.page"
            :last-page="lastPage"
            label="Días"
            testid="marketing-paginator"
            @change="irAPagina"
          />
        </AppCard>
      </template>
    </template>
  </div>
</template>
